import React, { useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import useDesktop from '@components/shared/useDesktop';
import { PathogenDataItem } from '@contracts';
import IconArrow from '@images/icon-arrow.svg';
import LazyLoadImage from '../LazyLoadImage';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

const EXPANDABLE_BREAKPOINT = 450;

interface PathogenCardProps {
  pathogenData: PathogenDataItem;
}

const PathogenCard: React.FC<PathogenCardProps> = ({ pathogenData }) => {
  const [isBodyCopyVisible, setIsBodyCopyVisible] = useState(false);
  const { t } = useTranslation();
  const desktop = useDesktop(EXPANDABLE_BREAKPOINT);

  return (
    <PathogenCardWrap
      $comingSoon={pathogenData.comingSoon ?? false}
      onClick={() => {
        setIsBodyCopyVisible((prevIsBodyCopyVisible) => !prevIsBodyCopyVisible);
      }}
    >
      <Head>
        <Image src={pathogenData?.image} alt={t(pathogenData.title)} />
        <div>
          {pathogenData.comingSoon && <ComingSoonLabel>{t('components.coming-soon')}</ComingSoonLabel>}
          <Title>{t(pathogenData.title)}</Title>
        </div>
        {!desktop && <IconRight $isIconRotated={isBodyCopyVisible} />}
      </Head>
      {(isBodyCopyVisible || desktop) && <BodyCopy $variant={TextVariant.BodyRegularLG}>{t(pathogenData.shortInfo)}</BodyCopy>}
      {pathogenData.onlySTI || pathogenData.onlyHPV ? (
        <HintText $variant={TextVariant.BodyRegularMD}>*{t(pathogenData.onlyHPV ? 'components.only-with-hpv' : 'components.only-with-sti')}</HintText>
      ) : null}
    </PathogenCardWrap>
  );
};

export default PathogenCard;

const PathogenCardWrap = styled.div<{ $comingSoon: boolean }>`
  background-color: ${(props) => (props.$comingSoon ? 'transparent' : Colors.paleGold25)};
  border: 2px solid ${Colors.ecru};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  text-align: left;
  max-width: 340px;
  flex-basis: 25%;

  @media (max-width: 800px) {
    max-width: 90vw;
    width: 500px;
  }

  @media (max-width: ${EXPANDABLE_BREAKPOINT}px) {
    cursor: pointer;
  }
`;

const Head = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  align-items: center;

  > div {
    display: flex;
    flex-direction: column;
    row-gap: 2px;
    align-items: flex-start;
    width: 100%;
  }
`;

const Image = styled(LazyLoadImage)`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

const IconRight = styled(IconArrow)<{ $isIconRotated: boolean }>`
  transform: ${(props) => (props.$isIconRotated ? 'rotate(180deg)' : 'none')};
  margin-top: ${(props) => (props.$isIconRotated ? '-3px' : '0')};
  width: 24px;
  height: 24px;
  object-fit: contain;
  transition: transform 0.3s ease-out;
`;

const ComingSoonLabel = styled.div`
  ${TEXT_STYLES[TextVariant.UpperText5]};
  font-size: 10px;
  color: ${Colors.navyBlue};
  background-color: ${Colors.ecru};
  border-radius: 2px;
  padding: 2px 6px;
`;

const Title = styled.h4`
  ${TEXT_STYLES[TextVariant.BodyBoldLG]};
`;
const BodyCopy = styled(Typography)`
  display: block;
`;

const HintText = styled(Typography)`
  opacity: 0.6;
  display: flex;
  align-items: flex-end;
  height: 100%;
`;
